import * as React from "react";
import { Search } from "lucide-react";
import { ShortcutManager } from "@authe/fe-commons/shortcuts";

const Palette = React.lazy(() => import("./HeaderSearchPalette").then((module) => ({ default: module.HeaderSearchPalette })));

/** Keep the entry path light; load search providers and results on first use. */
export function HeaderSearch(): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [requested, setRequested] = React.useState(false);
  const trigger = React.useRef<HTMLButtonElement>(null);
  const returnFocus = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    const manager = new ShortcutManager();
    manager.register({
      combo: "mod+k",
      description: "Open search",
      allowInInput: true,
      handler: () => {
        if (!open) returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        setRequested(true);
        setOpen(!open);
      },
    });
    return manager.attach();
  }, [open]);

  return <>
    <button
      ref={trigger}
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={() => { returnFocus.current = trigger.current; setRequested(true); setOpen(true); }}
      className="flex h-9 w-full max-w-md items-center gap-2 rounded-md border border-border bg-muted/40 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted"
    >
      <Search className="size-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate text-left">Search…</span>
      <kbd className="hidden shrink-0 rounded-sm border border-border bg-background px-1.5 text-3xs font-medium sm:inline">⌘K</kbd>
    </button>
    {requested && <React.Suspense fallback={open ? <span role="status" className="sr-only">Opening search…</span> : null}>
      <Palette open={open} onOpenChange={setOpen} onCloseAutoFocus={(event) => {
        event.preventDefault();
        // A shortcut can open search over another menu or from an editor.
        // Return to that invoker when it still exists, otherwise the trigger.
        const target = returnFocus.current;
        if (target?.isConnected && target !== document.body) target.focus();
        else trigger.current?.focus();
      }} />
    </React.Suspense>}
  </>;
}
